// Environment configuration utility for multi-region deployment
import globalConfig from '../environments/global/config.json';
import australiaConfig from '../environments/australia/config.json';

const getEnvironmentConfig = () => {
  const region = process.env.REACT_APP_REGION || 'global';
  const isProduction = process.env.NODE_ENV === 'production';
  const environment = isProduction ? 'production' : 'staging';
  
  const normalize = (url) => (url || '').replace(/\/+$/, '');
  
  const configs = {
    global: {
      ...globalConfig,
      apiBaseUrl: normalize(process.env.REACT_APP_API_BASE_URL || globalConfig.backend[environment]),
      googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
      environment
    },
    australia: {
      ...australiaConfig,
      apiBaseUrl: normalize(process.env.REACT_APP_API_BASE_URL || australiaConfig.backend[environment]),
      googleClientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      stripePublishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
      environment
    }
  };

  return configs[region] || configs.global;
};

// Australian states and their curriculum systems
const AUSTRALIAN_STATES = {
  'New South Wales': 'HSC',
  'Victoria': 'VCE'
};

const GLOBAL_COUNTRIES = [
  'Ireland',
  'United Kingdom',
  'France', 
  'Germany'
];

// Get appropriate countries based on app region
export const getCountriesForRegion = () => {
  return isAustralianRegion() ? Object.keys(AUSTRALIAN_STATES) : GLOBAL_COUNTRIES;
};

// Get default country for app region
export const getDefaultCountryForRegion = () => {
  return isAustralianRegion() ? 'New South Wales' : 'Ireland';
};

// Get curriculum system for Australian state
export const getCurriculumForState = (country) => {
  return AUSTRALIAN_STATES[country] || 'HSC'; // Default to HSC
};

// Check if country is an Australian state
export const isAustralianState = (country) => {
  return Object.keys(AUSTRALIAN_STATES).includes(country);
};

// Get effective country (app region overrides user country)
export const getEffectiveCountry = (userCountry) => {
  if (isAustralianRegion()) {
    // If user has an Australian state, use it; otherwise default to NSW
    return isAustralianState(userCountry) ? userCountry : 'New South Wales';
  }
  // For global app, use provided country or default
  return GLOBAL_COUNTRIES.includes(userCountry) ? userCountry : 'Ireland';
};

// Export individual config components for easier access
export const getCurrentConfig = getEnvironmentConfig;

// Dev region override utilities
export const getDevRegionOverride = () => {
  if (process.env.NODE_ENV !== 'development') return null;
  return localStorage.getItem('pulc_dev_region_override');
};

export const setDevRegionOverride = (region) => {
  if (process.env.NODE_ENV !== 'development') return;
  if (region === null) {
    localStorage.removeItem('pulc_dev_region_override');
  } else {
    localStorage.setItem('pulc_dev_region_override', region);
  }
};

// Helper function to get API headers with dev region override
export const getApiHeaders = (authToken = null) => {
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  
  // Add dev region override header in development
  if (process.env.NODE_ENV === 'development') {
    const devOverride = getDevRegionOverride();
    if (devOverride) {
      headers['X-Dev-Region'] = devOverride;
    }
  }
  
  return headers;
};

export const isAustralianRegion = () => {
  // In development, check for override first
  if (process.env.NODE_ENV === 'development') {
    const devOverride = getDevRegionOverride();
    const DEBUG_ENV = false;
    if (DEBUG_ENV) {
      console.log('�� isAustralianRegion - dev mode check:');
      console.log('  NODE_ENV:', process.env.NODE_ENV);
      console.log('  devOverride from localStorage:', devOverride);
      console.log('  localStorage raw value:', localStorage.getItem('pulc_dev_region_override'));
    }
    
    if (devOverride) {
      const result = devOverride === 'australia';
      if (DEBUG_ENV) console.log('  Using dev override:', devOverride, '-> isAustralian:', result);
      return result;
    } else {
      if (DEBUG_ENV) console.log('  No dev override, falling back to env var');
    }
  }
  
  const envRegion = (process.env.REACT_APP_REGION || 'global');
  const result = envRegion === 'australia';
  // Quiet by default to reduce log noise
  // console.log('�� isAustralianRegion - using env var:', envRegion, '-> isAustralian:', result);
  return result;
};

export const isGlobalRegion = () => (process.env.REACT_APP_REGION || 'global') === 'global';
export const getApiBaseUrl = () => getCurrentConfig().apiBaseUrl;
export const getCurrency = () => getCurrentConfig().currency;
export const getTimezone = () => getCurrentConfig().timezone;
export const getDomain = () => getCurrentConfig().domain;

export default getEnvironmentConfig; 